// ✅ src/pages/VehicleV2/LubeConsumptionAlert.jsx
// Production listing template

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaEye,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, createSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/lubeConsumptionAlert";

const LubeConsumptionAlert = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [vehicleType, setVehicleType] = useState("own");

  const [branches, setBranches] = useState([]);
  const [itemCategories, setItemCategories] = useState([]);
  const [itemNames, setItemNames] = useState([]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const isBusy = loadingInit || loadingList;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const [filters, setFilters] = useState({
    branch: sessionBranchType !== "HO" ? sessionBranchId : "",
    month: "September",
    year: "2025",
    vehicleRegNo: "",
    vehicleName: "",
    vehicleCode: "",
    vehicleMainType: "",
    itemCategory: "",
    itemName: "",
  });

  const fieldClass = `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const fetchInit = useCallback(async () => {
    try {
      setLoadingInit(true);
      const res = await axios.get(`${API_BASE}/init`, { headers: apiHeaders });
      if (!res.data?.success) throw new Error(res.data?.message || "Init failed");

      setBranches(res.data.branches || []);
      setItemNames(res.data.itemNames || []);
      setVehicleMainTypes(res.data.vehicleMainTypes || []);
      setMonths(res.data.months || []);
      setYears(res.data.years || []);

      const lockedCategory = res.data.itemCategory || null;
      const userLock = res.data.userLock || {};

      if (lockedCategory) {
        setItemCategories([
          { label: lockedCategory.name || "FUELS AND LUBRICANTS", value: lockedCategory.id || "Select" },
        ]);
      } else {
        setItemCategories([]);
      }

      setFilters((prev) => ({
        ...prev,
        branch: userLock.branchLocked && userLock.branchValue ? userLock.branchValue : prev.branch,
        itemCategory: lockedCategory?.id || prev.itemCategory || "Select",
      }));
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to load filters");
    } finally {
      setLoadingInit(false);
    }
  }, [apiHeaders]);

  const fetchList = useCallback(
    async ({ page = currentPage, pageSize = itemsPerPage } = {}) => {
      try {
        setLoadingList(true);
        const vehtype = vehicleType === "own" ? "O" : "H";
        const params = {
          page,
          pageSize,
          branchId: filters.branch || (sessionBranchType !== "HO" ? sessionBranchId : "Select"),
          monthText: filters.month,
          year: filters.year,
          vehicleRegNo: filters.vehicleRegNo || "",
          vehicleName: filters.vehicleName || "",
          vehicleCode: filters.vehicleCode || "",
          vehicleMainType: filters.vehicleMainType || "Select",
          itemCategoryId: filters.itemCategory || "Select",
          itemId: filters.itemName || "Select",
          vehtype,
        };
        const res = await axios.get(`${API_BASE}/list`, { params, headers: apiHeaders });
        if (!res.data?.success) throw new Error(res.data?.message || "List fetch failed");
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setCurrentPage(res.data.page || page);
      } catch (err) {
        console.error(err);
        toast.error(err.message || "Failed to load alerts");
        setRows([]);
        setTotal(0);
      } finally {
        setLoadingList(false);
      }
    },
    [apiHeaders, currentPage, filters, sessionBranchId, sessionBranchType, vehicleType]
  );

  useEffect(() => {
    fetchInit();
  }, [fetchInit]);

  const handleSearch = async () => {
    setCurrentPage(1);
    await fetchList({ page: 1, pageSize: itemsPerPage });
    toast.success("Search completed");
  };

  const handleViewAll = async () => {
    setFilters((prev) => ({
      ...prev,
      branch: sessionBranchType !== "HO" ? sessionBranchId : "",
      vehicleRegNo: "",
      vehicleName: "",
      vehicleCode: "",
      vehicleMainType: "",
      itemName: "",
    }));
    setCurrentPage(1);
    await fetchList({ page: 1, pageSize: itemsPerPage });
    toast.info("Showing all alerts");
  };

  const handleClearFilters = () => {
    setFilters({
      branch: sessionBranchType !== "HO" ? sessionBranchId : "",
      month: "September",
      year: "2025",
      vehicleRegNo: "",
      vehicleName: "",
      vehicleCode: "",
      vehicleMainType: "",
      itemCategory: itemCategories?.[0]?.value || "Select",
      itemName: "",
    });
    setRows([]);
    setTotal(0);
    setCurrentPage(1);
    toast.info("Filters cleared!");
  };

  const handlePrintReport = () => {
    const vehtype = vehicleType === "own" ? "O" : "H";
    const query = {
      vehtype,
      branchId: filters.branch || (sessionBranchType !== "HO" ? sessionBranchId : "Select"),
      monthText: filters.month,
      year: filters.year,
      vehicleRegNo: filters.vehicleRegNo || "",
      vehicleName: filters.vehicleName || "",
      vehicleCode: filters.vehicleCode || "",
      vehicleMainType: filters.vehicleMainType || "Select",
      itemCategoryId: filters.itemCategory || "Select",
      itemId: filters.itemName || "Select",
      page: "1",
      pageSize: "10000",
    };
    navigate({
      pathname: "/vehicle/LubeConsumptionAlert_Print",
      search: `?${createSearchParams(query).toString()}`,
    });
  };

  const handleRefresh = async () => {
    await fetchList({ page: currentPage, pageSize: itemsPerPage });
  };

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const indexOfFirstItem = total === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, total);

  const getStatusClass = (status) => {
    switch (status) {
      case "Critical":
        return "bg-red-100 text-red-800";
      case "Warning":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-green-100 text-green-800";
    }
  };

  const monthOptions = months.length
    ? months.map((m) => m.text || m.label)
    : ["January","February","March","April","May","June","July","August","September","October","November","December"];

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">LUBE CONSUMPTION ALERT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>
                Lube Consumption Alert
              </h2>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handlePrintReport} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}>
                <FaPrint /> Print
              </button>
              <button onClick={handleRefresh} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaSyncAlt /> Refresh
              </button>
              <button onClick={() => navigate(-1)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                <FaArrowLeft /> Back
              </button>
              <button onClick={() => setShowFilters((s) => !s)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClearFilters} type="button" className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"}`}>Clear</button>
                  <button onClick={handleViewAll} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} disabled={isBusy} type="button" className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSearch /> {loadingList ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="mb-4">
                <label className={labelClass}>Vehicle Type</label>
                <div className="flex gap-6">
                  <label className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <input type="radio" name="vehicleType" className="mr-2" checked={vehicleType === "own"} onChange={() => { setVehicleType("own"); setCurrentPage(1); }} />
                    Own Vehicle
                  </label>
                  <label className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <input type="radio" name="vehicleType" className="mr-2" checked={vehicleType === "hire"} onChange={() => { setVehicleType("hire"); setCurrentPage(1); }} />
                    Hire Vehicle
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select name="branch" value={filters.branch} onChange={handleChange} className={fieldClass} disabled={sessionBranchType !== "HO" || isBusy}>
                    <option value="">Select</option>
                    {branches.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    {monthOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    {(years.length ? years : ["2025", "2026"]).map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Reg No</label>
                  <input type="text" name="vehicleRegNo" value={filters.vehicleRegNo} onChange={handleChange} className={fieldClass} placeholder="Reg No" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Name</label>
                  <input type="text" name="vehicleName" value={filters.vehicleName} onChange={handleChange} className={fieldClass} placeholder="Name" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Code</label>
                  <input type="text" name="vehicleCode" value={filters.vehicleCode} onChange={handleChange} className={fieldClass} placeholder="Code" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select name="vehicleMainType" value={filters.vehicleMainType} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="">Select</option>
                    {vehicleMainTypes.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Item Category</label>
                  <select name="itemCategory" value={filters.itemCategory} onChange={handleChange} className={fieldClass} disabled>
                    {itemCategories.length
                      ? itemCategories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)
                      : <option value="Select">FUELS AND LUBRICANTS</option>}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Item Name</label>
                  <select name="itemName" value={filters.itemName} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="">Select</option>
                    {itemNames.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
              Total Alerts: {total}
            </div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {loadingInit ? "Loading dropdowns..." : loadingList ? "Searching..." : "Ready"}
            </div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {loadingInit || loadingList ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Alerts Found</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Adjust filters or click Search.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Branch</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Vehicle Reg No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Vehicle Code</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Item Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Current Level</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Last Refill</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {rows.map((item, index) => (
                    <tr key={item.id || `${item.vehicleRegNo}-${item.itemName}-${index}`} className={`${index % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{item.branch}</td>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{item.vehicleRegNo}</td>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{item.vehicleCode}</td>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{item.itemName}</td>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{item.currentLevel}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(item.status)}`}>{item.status}</span>
                      </td>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{item.lastRefill}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {total > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {indexOfFirstItem} to {indexOfLastItem} of {total} entries
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { const p = Math.max(1, currentPage - 1); setCurrentPage(p); fetchList({ page: p }); }} disabled={currentPage === 1 || isBusy} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 || isBusy ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => { const p = Math.min(totalPages, currentPage + 1); setCurrentPage(p); fetchList({ page: p }); }} disabled={currentPage === totalPages || isBusy} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages || isBusy ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default LubeConsumptionAlert;